import { ViteReactSSG } from 'vite-react-ssg'
import { BrowserRouter } from 'react-router-dom'
import App from './App'
import './index.css'
import { IntlProvider } from './i18n/IntlProvider'
import { LanguageProvider, SUPPORTED_LANGUAGES, SupportedLanguage } from './i18n/LanguageContext'
import { StrictMode } from 'react'
import { HeadProvider } from './components/Head'
import { SEOHead, PageKey } from './components/SEOHead'
import { enableHydrationDebugging } from './utils/hydrationDebug'
import LandingPage from './pages/LandingPage'
import ContactPage from './pages/ContactPage'
import FeaturesPage from './pages/FeaturesPage'
import FaqPage from './pages/FaqPage'
import PricingPage from './pages/PricingPage'
import PrivacyPolicyPage from './pages/PrivacyPolicyPage'
import TermsOfServicePage from './pages/TermsOfServicePage'

import InvoiceStatusPage from './pages/InvoiceStatusPage'
import type { RouteRecord } from 'vite-react-ssg'
import * as Sentry from "@sentry/react"

// Initialize Sentry for error tracking
Sentry.init({
  dsn: "https://355dfc170e50cf56b2063fa59338d6d0@o4509402474676224.ingest.de.sentry.io/4509402475987024",
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true
});

// Enable hydration debugging in development
if (import.meta.env.DEV) {
  enableHydrationDebugging();
}

// CLIENT-SIDE INITIALIZATION
// This runs in the browser and provides full SPA functionality
if (typeof window !== 'undefined') {
  const rootElement = document.getElementById('root')!;
  
  // Create the full App with all functionality
  const ClientApp = () => (
    <StrictMode>
      <BrowserRouter>
        <HeadProvider>
          <LanguageProvider>
            <IntlProvider>
              <App />
            </IntlProvider>
          </LanguageProvider>
        </HeadProvider>
      </BrowserRouter>
    </StrictMode>
  );

  // Initialize React root for client-side
  import('react-dom/client').then(({ createRoot }) => {
    console.log('[Client] Initializing full SPA with stores, auth, and routing');
    createRoot(rootElement).render(<ClientApp />);
  });
}

// SSG CONFIGURATION (BUILD-TIME ONLY)
// This only runs during build to generate static HTML for SEO

// Wrap components for SSG with proper providers and SEO
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const wrapForSSG = (Component: React.ComponentType<any>, pageKey: PageKey) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (props: any) => {
    const lang = props.lang || 'en';
    
    return (
      <StrictMode>
        <HeadProvider>
          <LanguageProvider initialLanguage={lang as SupportedLanguage}>
            <IntlProvider initialLanguage={lang as SupportedLanguage}>
              <SEOHead pageKey={pageKey} />
              <Component {...props} />
            </IntlProvider>
          </LanguageProvider>
        </HeadProvider>
      </StrictMode>
    );
  };
};

// SSG routes for static generation (SEO purposes only)
export const routes: RouteRecord[] = [
  {
    path: '/',
    element: wrapForSSG(LandingPage, 'home')({}),
    entry: 'src/pages/LandingPage.tsx'
  },
  {
    path: '/contact',
    element: wrapForSSG(ContactPage, 'contact')({}),
    entry: 'src/pages/ContactPage.tsx'
  },
  {
    path: '/features',
    element: wrapForSSG(FeaturesPage, 'features')({}),
    entry: 'src/pages/FeaturesPage.tsx'
  },
  {
    path: '/faq',
    element: wrapForSSG(FaqPage, 'faq')({}),
    entry: 'src/pages/FaqPage.tsx'
  },
  {
    path: '/pricing',
    element: wrapForSSG(PricingPage, 'pricing')({}),
    entry: 'src/pages/PricingPage.tsx'
  },
  {
    path: '/privacy',
    element: wrapForSSG(PrivacyPolicyPage, 'privacyPolicy')({}),
    entry: 'src/pages/PrivacyPolicyPage.tsx'
  },
  {
    path: '/terms',
    element: wrapForSSG(TermsOfServicePage, 'termsOfService')({}),
    entry: 'src/pages/TermsOfServicePage.tsx'
  },
  {
    path: '/invoice-status/:id',
    element: wrapForSSG(InvoiceStatusPage, 'invoiceStatus')({}),
    entry: 'src/pages/InvoiceStatusPage.tsx'
  },
  // Multi-language routes
  ...SUPPORTED_LANGUAGES.filter(lang => lang !== 'en').flatMap(lang => [
    {
      path: `/${lang}`,
      element: wrapForSSG(LandingPage, 'home')({ lang }),
      entry: 'src/pages/LandingPage.tsx'
    },
    {
      path: `/${lang}/contact`,
      element: wrapForSSG(ContactPage, 'contact')({ lang }),
      entry: 'src/pages/ContactPage.tsx'
    },
    {
      path: `/${lang}/features`,
      element: wrapForSSG(FeaturesPage, 'features')({ lang }),
      entry: 'src/pages/FeaturesPage.tsx'
    },
    {
      path: `/${lang}/faq`,
      element: wrapForSSG(FaqPage, 'faq')({ lang }),
      entry: 'src/pages/FaqPage.tsx'
    },
    {
      path: `/${lang}/pricing`,
      element: wrapForSSG(PricingPage, 'pricing')({ lang }),
      entry: 'src/pages/PricingPage.tsx'
    },
    {
      path: `/${lang}/privacy`,
      element: wrapForSSG(PrivacyPolicyPage, 'privacyPolicy')({ lang }),
      entry: 'src/pages/PrivacyPolicyPage.tsx'
    },
    {
      path: `/${lang}/terms`,
      element: wrapForSSG(TermsOfServicePage, 'termsOfService')({ lang }),
      entry: 'src/pages/TermsOfServicePage.tsx'
    }
  ])
];

// ONLY export SSG during build time, not in browser
export const createRoot = typeof window === 'undefined' ? ViteReactSSG(
  { routes },
  () => {
    console.log('[SSG] Build-time static generation for SEO');
  }
) : undefined;
